import React, { useState, useEffect } from 'react';
import { academyAdminAPI } from '../../lib/academyApi';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, X, Users } from 'lucide-react';
import ImagePicker from '../../components/cms/ImagePicker';
import SignaturePad from '../../components/shared/SignaturePad';

const AcademyInstructors = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', title: '', bio: '', photo_url: '', signature_url: '', expertise_areas: '', email: '', linkedin: '', order: 0, is_active: true });

  const fetchData = async () => { try { const r = await academyAdminAPI.listInstructors(); setItems(r.data); } catch(e){} finally { setLoading(false); } };
  useEffect(() => { fetchData(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: '', slug: '', title: '', bio: '', photo_url: '', signature_url: '', expertise_areas: '', email: '', linkedin: '', order: 0, is_active: true }); setShowForm(true); };
  const openEdit = (item) => { setEditing(item.id); setForm({ ...item, expertise_areas: (item.expertise_areas || []).join(', ') }); setShowForm(true); };

  const handleSave = async () => {
    if (!form.name || !form.slug) { toast.error('Nom et slug requis'); return; }
    const payload = { ...form, expertise_areas: form.expertise_areas.split(',').map(s => s.trim()).filter(Boolean) };
    try {
      if (editing) { await academyAdminAPI.updateInstructor(editing, payload); toast.success('Mis a jour'); }
      else { await academyAdminAPI.createInstructor(payload); toast.success('Cree'); }
      setShowForm(false); fetchData();
    } catch(e) { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer?')) return;
    try { await academyAdminAPI.deleteInstructor(id); toast.success('Supprime'); fetchData(); } catch(e) { toast.error('Erreur'); }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" /></div>;

  return (
    <div className="p-6 lg:p-8" data-testid="academy-instructors-admin">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading text-2xl font-bold text-[#1D1D1B]">Instructeurs</h1><p className="text-neutral-500 text-sm">{items.length} instructeur(s)</p></div>
        <Button onClick={openNew} data-testid="add-instructor-btn"><Plus className="w-4 h-4 mr-2" />Ajouter</Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map(item => (
          <Card key={item.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 bg-neutral-100">
                {item.photo_url ? <img src={item.photo_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Users className="w-6 h-6 text-neutral-300" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-[#1D1D1B]">{item.name}</h3>
                <p className="text-sm text-[#F9B438]">{item.title}</p>
                <div className="flex flex-wrap gap-1 mt-1">{item.expertise_areas?.slice(0,3).map((a,i) => <Badge key={i} variant="outline" className="text-xs">{a}</Badge>)}</div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button variant="ghost" size="sm" onClick={() => openEdit(item)}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(item.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowForm(false)} />
          <Card className="relative w-full max-w-lg z-10">
            <CardHeader className="flex flex-row items-center justify-between"><CardTitle>{editing ? 'Modifier' : 'Ajouter'}</CardTitle><Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button></CardHeader>
            <CardContent className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Nom *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value, slug: form.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]/g,'-')})} /></div>
                <div><Label>Slug *</Label><Input value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} /></div>
              </div>
              <div><Label>Titre / Poste</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} /></div>
              <div><Label>Biographie</Label><Textarea value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} rows={3} /></div>
              <div><Label>Domaines d'expertise (virgules)</Label><Input value={form.expertise_areas} onChange={e => setForm({...form, expertise_areas: e.target.value})} placeholder="PCSWMM, Hydrologie, ..." /></div>
              <ImagePicker value={form.photo_url} onChange={(url) => setForm({...form, photo_url: url})} folder="instructors" label="Photo" />
              <SignaturePad value={form.signature_url} onChange={(url) => setForm({...form, signature_url: url})} instructorId={editing} label="Signature (pour les certificats)" />
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Email</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
                <div><Label>LinkedIn</Label><Input value={form.linkedin} onChange={e => setForm({...form, linkedin: e.target.value})} /></div>
              </div>
              <div className="flex items-center gap-2"><input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} /><Label>Actif</Label></div>
              <div className="flex gap-3"><Button variant="outline" onClick={() => setShowForm(false)} className="flex-1">Annuler</Button><Button onClick={handleSave} className="flex-1">Enregistrer</Button></div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AcademyInstructors;
